import React, { useState, useRef, useEffect } from 'react';
import { ReactComponent as ImageIcon } from '../../../assets/icons/image.svg';
import { useUploadImageMutation } from '../../../redux/survey/survey.endpoints';
import { ALLOWED_IMAGE_TYPE_STRING, ALLOWED_IMAGE_TYPES } from 'src/components/image-upload/image-uploads.constants';
import { Brand, SurveyToast } from 'src/redux/survey/survey.types';

type ImageType = 'brand_1' | 'brand_2' | 'toast' | 'default_toast';

interface SurveyImageUploadProps {
  src: string | null;
  viewImage?: boolean;
  brand?: Brand | null;
  toast?: SurveyToast | null;
  handleEditBrand?: (brand: Brand | null) => void;
  handleEditToast?: (toast: Partial<SurveyToast>) => void;
  type: ImageType;
  disabled?: boolean;
}

const LogoUploader: React.FC<SurveyImageUploadProps> = ({
  src,
  viewImage,
  brand,
  toast,
  handleEditBrand,
  handleEditToast,
  type,
  disabled,
}) => {
  const [uploadedImageLocal, setUploadedImageLocal] = useState<string | null>(src);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadImage] = useUploadImageMutation();

  useEffect(() => {
    setUploadedImageLocal(src);
  }, [src]);

  useEffect(() => {
    const handleUpload = async() => {
      if ((type === 'toast' && !toast) || (type !== 'toast' && !brand)) return;
      if (uploadedImage) {
        try {
          const formData = new FormData();
          formData.append('file', uploadedImage);
          formData.append('type', type);
          formData.append('uuid', type === 'toast' ? (toast?.id || '') : (brand?.id || ''));
          const response = await uploadImage({ formData: formData, uuid: type === 'toast' ? (toast?.id || '') : (brand?.id || '') }).unwrap();
          const imageUrl = response.item.url;
          setUploadedImageLocal(imageUrl);
          if (type === 'toast' && handleEditToast && toast) {
            handleEditToast({ image_url: imageUrl });
          } else if (handleEditBrand && brand) {
            handleEditBrand({
              ...brand,
              ...(type === 'brand_1' ? { image_1: imageUrl } :
                type === 'brand_2' ? { image_2: imageUrl } :
                  { toast_image: imageUrl }),
            });
          }
        } catch (error) {
          console.error('Upload Error:', error);
        }
      }
    };
    handleUpload();
  }, [uploadedImage, uploadImage]);

  const handleImageUpload = (file: File) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImageLocal(reader.result as string);
        setUploadedImage(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files?.[0];
    if (file && ALLOWED_IMAGE_TYPES.includes(file.type)) {
      handleImageUpload(file);
    }
  };

  const renderViewImage = () => {
    return (
      <div
        className="relative flex h-32 w-full items-center justify-center overflow-hidden rounded-lg bg-white shadow-md"
      >
        {src ? (
          <img
            src={src}
            alt="Uploaded"
            className="object-contain p-20 max-h-full max-w-full"
          />
        ) : (
          <div className="text-gray-401">
            No Image Available
          </div>
        )}
      </div>
    );
  };

  const renderImageUpload = () => {
    return (
      <div>
        {!uploadedImageLocal ? (
          <div
            className={`relative flex h-32 w-full items-center justify-center overflow-hidden rounded-lg bg-white shadow-md ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            onClick={() => !disabled && fileInputRef.current?.click()}
            onDragOver={!disabled ? handleDragOver : undefined}
            onDrop={!disabled ? handleDrop : undefined}
          >
            <span className="ml-2 flex items-center space-x-2 text-gray-401">
              <ImageIcon className='size-4 object-contain fill-black opacity-50' />
              <div className="flex">
                <span className="mr-1 underline">
                  {type === 'toast' ? 'Upload toast image' :
                    type === 'default_toast' ? 'Upload default toast image' :
                      `Upload brand ${type === 'brand_1' ? 'logo' : 'icon'}`}
                </span>
              </div>
            </span>
            <div className='absolute bottom-3 left-0 right-0 text-center text-gray-401 text-xs'>
              Please upload an image (png, jpg, jpeg) under 500 Kb.
            </div>
            <input
              ref={fileInputRef}
              id="upload_survey_image"
              type="file"
              accept={ALLOWED_IMAGE_TYPE_STRING}
              className="absolute inset-0 hidden"
              onChange={(e) => !disabled && handleImageUpload(e.target.files?.[0] as File)}
              disabled={disabled}
            />
          </div>
        ) : (
          <div
            className={`relative flex h-32 w-full items-center justify-center overflow-hidden rounded-lg bg-white shadow-md ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          >
            <img
              src={uploadedImageLocal || ''}
              alt="Uploaded"
              className="object-contain p-2 w-full h-full max-h-full max-w-full"
            />
            <input
              id="upload_survey_image"
              type="file"
              accept={ALLOWED_IMAGE_TYPE_STRING}
              className={`absolute inset-0 opacity-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              onChange={(e) => !disabled && handleImageUpload(e.target.files?.[0] as File)}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {viewImage ? renderViewImage() : renderImageUpload()}
    </div>
  );
};

export default LogoUploader;
